import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../lib/api';
import { bus } from '../../App';
import { TextContentViewer } from '../TextContentViewer';
import { ImageContentViewer } from '../ImageContentViewer';
import { useSelectionStore } from '../../state/selectionStore';
import { usePanelContext } from '../layout/PanelContext';

function highlightCode(code: string, language: string): string {
  if (language === 'html') {
    return code
      .replace(/&lt;(\/?([\w-]+))/g, '&lt;<span class="hl-tag">$1</span>')
      .replace(/([\w-]+)=(&quot;[^&]*&quot;|&#39;[^&]*&#39;)/g, '<span class="hl-attr">$1</span>=<span class="hl-string">$2</span>')
      .replace(/&lt;!--(.*?)--&gt;/g, '<span class="hl-comment">&lt;!--$1--&gt;</span>');
  }

  if (language === 'javascript' || language === 'js' || language === 'ts' || language === 'tsx') {
    const placeholders: string[] = [];
    let result = code;

    // Step 1: Extract and protect strings
    result = result.replace(/(&quot;(?:[^&]|&(?!quot;))*&quot;|&#39;(?:[^&]|&(?!#39;))*&#39;|`(?:[^`])*`)/g, (match) => {
      const index = placeholders.length;
      placeholders.push(`<span class="hl-string">${match}</span>`);
      return `___PLACEHOLDER_${index}___`;
    });

    // Step 2: Extract and protect comments
    result = result.replace(/(\/\/.*$)/gm, (match) => {
      const index = placeholders.length;
      placeholders.push(`<span class="hl-comment">${match}</span>`);
      return `___PLACEHOLDER_${index}___`;
    });
    result = result.replace(/(\/\*[\s\S]*?\*\/)/g, (match) => {
      const index = placeholders.length;
      placeholders.push(`<span class="hl-comment">${match}</span>`);
      return `___PLACEHOLDER_${index}___`;
    });

    // Step 3: Highlight keywords and literals (now safe from strings/comments)
    result = result
      .replace(/\b(const|let|var|function|class|return|if|else|for|while|async|await|import|export|from|default)\b/g, '<span class="hl-keyword">$1</span>')
      .replace(/\b(true|false|null|undefined)\b/g, '<span class="hl-literal">$1</span>');

    // Step 4: Restore placeholders
    placeholders.forEach((placeholder, index) => {
      result = result.replace(`___PLACEHOLDER_${index}___`, placeholder);
    });

    return result;
  }

  if (language === 'css') {
    const placeholders: string[] = [];
    let result = code;

    // Step 1: Extract and protect comments
    result = result.replace(/(\/\*[\s\S]*?\*\/)/g, (match) => {
      const index = placeholders.length;
      placeholders.push(`<span class="hl-comment">${match}</span>`);
      return `___PLACEHOLDER_${index}___`;
    });

    // Step 2: Highlight selectors, properties, values
    result = result
      .replace(/([\w-]+)\s*\{/g, '<span class="hl-selector">$1</span> {')
      .replace(/([\w-]+):/g, '<span class="hl-property">$1</span>:')
      .replace(/:\s*([^;]+);/g, ': <span class="hl-value">$1</span>;');

    // Step 3: Restore placeholders
    placeholders.forEach((placeholder, index) => {
      result = result.replace(`___PLACEHOLDER_${index}___`, placeholder);
    });

    return result;
  }

  return code;
}

function getLanguageFromPath(path: string): string {
  const ext = path.split('.').pop()?.toLowerCase() || '';
  const langMap: Record<string, string> = {
    'html': 'html',
    'htm': 'html',
    'js': 'javascript',
    'jsx': 'javascript',
    'ts': 'javascript',
    'tsx': 'javascript',
    'css': 'css',
    'scss': 'css',
    'sass': 'css'
  };
  return langMap[ext] || 'text';
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const IMAGE_MIME_MAP: Record<string, string> = {
  avif: 'image/avif',
  bmp: 'image/bmp',
  gif: 'image/gif',
  ico: 'image/x-icon',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  svg: 'image/svg+xml',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  webp: 'image/webp'
};

function isImagePath(path: string | null): boolean {
  if (!path) {
    return false;
  }
  const ext = path.split('.').pop()?.toLowerCase() || '';
  return Boolean(IMAGE_MIME_MAP[ext]);
}

function getImageMimeType(path: string | null): string {
  if (!path) {
    return 'application/octet-stream';
  }
  const ext = path.split('.').pop()?.toLowerCase() || '';
  return IMAGE_MIME_MAP[ext] || 'application/octet-stream';
}

function normalizeBinaryPayload(value: unknown): Uint8Array | null {
  if (!value) {
    return null;
  }
  if (value instanceof Uint8Array) {
    return value;
  }
  if (value instanceof ArrayBuffer) {
    return new Uint8Array(value);
  }
  if (ArrayBuffer.isView(value)) {
    const view = value as ArrayBufferView;
    return new Uint8Array(view.buffer.slice(view.byteOffset, view.byteOffset + view.byteLength));
  }
  return null;
}

export function FilePanel() {
  const [fileContent, setFileContent] = useState<string>('');
  const [imageContent, setImageContent] = useState<Uint8Array | null>(null);
  const [imageMime, setImageMime] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const filePath = useSelectionStore((state) => state.file_path);
  const searchResult = useSelectionStore((state) => state.search_result);
  const select = useSelectionStore((state) => state.set);
  const { panelId, setTitle } = usePanelContext();
  const isImageFile = useMemo(() => isImagePath(filePath ?? null), [filePath]);
  const fileLanguage = useMemo(() => {
    if (!filePath || isImageFile) {
      return 'text';
    }
    return getLanguageFromPath(filePath);
  }, [filePath, isImageFile]);

  useEffect(() => {
    let cancelled = false;

    const loadFile = async () => {
      if (!filePath) {
        setFileContent('');
        setImageContent(null);
        setImageMime(null);
        setLoading(false);
        setTitle?.(panelId, '');
        return;
      }

      setLoading(true);
      setTitle?.(panelId, filePath);

      try {
        const client = api();
        if (isImageFile) {
          const binary = await client.services.files.readBinary(filePath);
          if (!cancelled) {
            setImageContent(normalizeBinaryPayload(binary));
            setImageMime(getImageMimeType(filePath));
            setFileContent('');
          }
          return;
        }

        const content = await client.services.files.read(filePath);
        if (!cancelled) {
          setFileContent(content || '');
          setImageContent(null);
          setImageMime(null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('[FilePanel] Failed to load file:', error);
          setFileContent('');
          setImageContent(null);
          setImageMime(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadFile();

    return () => {
      cancelled = true;
    };
  }, [filePath, panelId, setTitle, reloadTrigger, isImageFile]);

  useEffect(() => {
    if (!searchResult) {
      return;
    }
    if (searchResult.path === filePath) {
      return;
    }
    select({ file_path: searchResult.path });
  }, [searchResult, filePath, select]);

  useEffect(() => {
    const unsubscribe = bus.on('files/checkout', () => {
      setReloadTrigger(prev => prev + 1);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleSave = useCallback(async (newContent: string) => {
    if (!filePath || isImageFile) return;
    try {
      await api().services.files.create(filePath, newContent);
      setFileContent(newContent);
      select({ file_path: filePath });
    } catch (error) {
      console.error('[FilePanel] Failed to save file:', error);
      throw error;
    }
  }, [filePath, select, isImageFile]);

  const renderContent = useCallback((content: string) => {
    const escapedContent = escapeHtml(content);
    return fileLanguage !== 'text'
      ? highlightCode(escapedContent, fileLanguage)
      : escapedContent;
  }, [fileLanguage]);

  const searchSelection = useMemo(() => {
    if (!searchResult) {
      return null;
    }
    if (searchResult.path !== filePath) {
      return null;
    }
    const matchLength = typeof searchResult.match === 'string' ? searchResult.match.length : 0;
    return {
      line: searchResult.line,
      column: searchResult.column,
      length: matchLength > 0 ? matchLength : 1
    };
  }, [searchResult, filePath]);

  if (!filePath) {
    return (
      <div className="panel-shell">
        <div className="empty-state">
          <p>No file selected</p>
          <p className="empty-state-subtitle">Select a file from the file explorer</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="panel-shell">
        <div className="empty-state">
          <p>Loading file...</p>
        </div>
      </div>
    );
  }

  if (isImageFile) {
    return (
      <div className="panel-shell">
        {imageContent ? (
          <ImageContentViewer
            data={imageContent}
            fileName={filePath}
            mimeType={imageMime || getImageMimeType(filePath)}
          />
        ) : (
          <div className="image-content-viewer">
            <div className="image-content-viewer__message">
              <p>Unable to load image</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="panel-shell">
      <TextContentViewer
        content={fileContent}
        onSave={handleSave}
        renderContent={renderContent}
        showLineNumbers
        language={fileLanguage === 'text' ? undefined : fileLanguage}
        focusLocation={searchSelection}
      />
    </div>
  );
}
